import { Fragment } from "react";
import { RefreshCw, Scissors, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { CollapsibleSection } from "@/components/zakazka/CollapsibleSection";
import { extractErrorMessage } from "@/api/client";
import { formatM2, formatPct } from "@/lib/format";
import {
  useOdpadVCene,
  usePorezVysledok,
  useSpustiPorez,
  useZrusPorez,
} from "@/api/hooks/usePorez";
import { useNarezPlan } from "@/api/hooks/useNarez";
import type {
  NarezMaterialItem,
  PorezCutRiteMaterial,
  PorezMaterialItem,
  PorezVysledok,
} from "@/api/schemas";

export interface PorezPanelProps {
  variantaId: string;
  /** V tabovom paneli je titulok tab a roleta by bola druhá úroveň skladania. */
  collapsible?: boolean;
}

/** Jeden riadok tabuľky — materiál z porezu Rozpočtu, plánu Cut Rite alebo oboch. */
interface RiadokMaterialu {
  kluc: string;
  nazov: string;
  titulok: string;
  porez?: PorezMaterialItem;
  /** strata tabule z porezu (odpad + zvyšky, % z plochy tabúľ) */
  porezPct?: number;
  cutrite?: PorezCutRiteMaterial;
  /** odkiaľ ide % do ceny; `undefined` = paušál z cenníka */
  vCene?: "cutrite" | "porez";
}

const pct1 = (v: number): string => formatPct(v, 1);

function riadkyMaterialov(vysledok: PorezVysledok | undefined): RiadokMaterialu[] {
  if (!vysledok) return [];
  const vCene = new Map(
    (vysledok.v_cene ?? []).map((x) => [x.material_id, x.zdroj as "cutrite" | "porez"]),
  );
  const riadky = new Map<string, RiadokMaterialu>();
  for (const m of vysledok.materialy ?? []) {
    const tabuleM2 = (m.pocet_tabul * m.tabula_x_mm * m.tabula_y_mm) / 1_000_000;
    const strata =
      tabuleM2 > 0
        ? ((parseFloat(String(m.odpad_m2 ?? 0)) + parseFloat(String(m.zvysky_m2 ?? 0))) /
            tabuleM2) *
          100
        : 0;
    riadky.set(m.material.id, {
      kluc: m.material.id,
      nazov: `${m.material.nazov}${m.fallback_format ? " ⚠" : ""}`,
      titulok: `${m.material.nazov} — tabuľa ${m.tabula_x_mm}×${m.tabula_y_mm} mm · dielce ${formatM2(m.plocha_dielcov_m2)} m² · zvyšky ${formatM2(m.zvysky_m2)} m² · odpad ${formatM2(m.odpad_m2)} m² · výťažnosť ${formatPct(m.vytaznost_celkova_pct, 1)}`,
      porez: m,
      porezPct: strata,
      vCene: vCene.get(m.material.id),
    });
  }
  for (const c of vysledok.cutrite?.materialy ?? []) {
    const kluc = c.material_id ?? `kod:${c.kod}`;
    const riadok = riadky.get(kluc) ?? {
      kluc,
      nazov: c.popis || c.kod,
      titulok: c.material_id
        ? `${c.kod} — ${c.popis}`
        : `${c.kod} — v zákazke materiál s týmto kódom nie je`,
      vCene: c.material_id ? vCene.get(c.material_id) : undefined,
    };
    riadky.set(kluc, { ...riadok, cutrite: c });
  }
  return [...riadky.values()];
}

function datum(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("sk-SK", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Porez plošných a plán z Cut Rite vedľa seba (KOLO 213) — koľko tabúľ
 * a aký odpad vyšiel v Rozpočte a koľko ich naozaj použil Cut Rite.
 * Do ceny ide plán z Cut Rite, kým sa rezanie nezmení, inak porez
 * Rozpočtu; prepínač „Odpad v cene" vráti cenu na paušál z cenníka.
 */
export function PorezPanel({
  variantaId,
  collapsible = true,
}: PorezPanelProps): JSX.Element {
  const { data } = usePorezVysledok(variantaId);
  const spusti = useSpustiPorez(variantaId);
  const zrus = useZrusPorez(variantaId);
  const odpadVCene = useOdpadVCene(variantaId);

  const vysledok = data?.data;
  const maVysledok = (vysledok?.materialy?.length ?? 0) > 0;
  const cutrite = vysledok?.cutrite ?? null;
  const pausal = vysledok?.odpad_v_cene === "pausal";
  const riadky = riadkyMaterialov(vysledok);
  const cutriteVCene = (vysledok?.v_cene ?? []).some((x) => x.zdroj === "cutrite");

  function handleSpusti(): void {
    spusti.mutate(undefined, {
      onSuccess: () =>
        toast({
          title: "Porez vypočítaný",
          description: "Reálny odpad materiálu vstúpil do ceny.",
        }),
      onError: (err) =>
        toast({
          variant: "destructive",
          title: "Chyba pri výpočte porezu",
          description: extractErrorMessage(err),
        }),
    });
  }

  function handleZrus(): void {
    zrus.mutate(undefined, {
      onSuccess: () =>
        toast({
          title: "Porez zrušený",
          description: "Ceny používajú predvolenú nadmieru materiálov.",
        }),
      onError: (err) =>
        toast({
          variant: "destructive",
          title: "Chyba pri zrušení porezu",
          description: extractErrorMessage(err),
        }),
    });
  }

  function handleOdpad(odpad: "porez" | "pausal"): void {
    if (vysledok?.odpad_v_cene === odpad) return;
    odpadVCene.mutate(odpad, {
      onSuccess: () =>
        toast({
          title: odpad === "pausal" ? "Odpad v cene: paušál" : "Odpad v cene: podľa plánu",
          description:
            odpad === "pausal"
              ? "Cena berie nadmieru z cenníka (uni 20 % / dekor 30 %)."
              : "Cena berie plán z Cut Rite, inak porez Rozpočtu.",
        }),
      onError: (err) =>
        toast({
          variant: "destructive",
          title: "Zmena sa neuložila",
          description: extractErrorMessage(err),
        }),
    });
  }

  // Badge ide do `action` slotu rolety, aby stav bolo vidno aj zbalené.
  const badge = pausal ? (
    <span
      className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground"
      title="Odpad v cene: paušál z cenníka (uni 20 % / dekor 30 %)"
    >
      Paušál
    </span>
  ) : cutriteVCene ? (
    <span
      className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
      title="Odpad z plánu Cut Rite vstupuje do ceny"
    >
      Cut Rite v cene
    </span>
  ) : maVysledok ? (
    vysledok?.aktualny ? (
      <span
        className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
        title="Vypočítaný odpad z porezu vstupuje do ceny"
      >
        Použité v cene
      </span>
    ) : (
      <span
        className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900 dark:text-amber-200"
        title="Dielce sa od výpočtu zmenili — ceny používajú predvolenú nadmieru"
      >
        Neaktuálne
      </span>
    )
  ) : null;

  const pozor =
    "rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200";

  return (
    <PanelObal collapsible={collapsible} badge={badge}>
      <div className="space-y-3 pt-1">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Plošné (tabule)
      </div>
      {vysledok ? (
        <div className="space-y-1">
          <div className="text-[11px] text-muted-foreground">Odpad v cene</div>
          <div
            role="radiogroup"
            aria-label="Odpad v cene"
            className="inline-flex rounded-md border p-0.5 text-xs"
          >
            {(
              [
                ["porez", "Podľa plánu", "Plán z Cut Rite, kým sa rezanie nezmení; inak porez Rozpočtu"],
                ["pausal", "Paušál z cenníka", "Nadmiera z cenníka — uni 20 % / dekor 30 %"],
              ] as const
            ).map(([hodnota, nazov, titulok]) => {
              const zvolene = (vysledok.odpad_v_cene ?? "porez") === hodnota;
              return (
                <button
                  key={hodnota}
                  type="button"
                  role="radio"
                  aria-checked={zvolene}
                  title={titulok}
                  disabled={odpadVCene.isPending}
                  onClick={() => handleOdpad(hodnota)}
                  className={`rounded px-2 py-0.5 ${
                    zvolene
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {nazov}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {riadky.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Rozloží plošné dielce na tabule ako Cut Rite (prerez 4,4, orez 15)
          a do ceny dá reálny odpad namiesto predvolenej nadmiery (uni 20 % /
          dekor 30 %). Po optimalizácii v Cut Rite sa vedľa ukáže jeho plán.
        </p>
      ) : (
        <>
          {maVysledok && vysledok && !vysledok.aktualny ? (
            <p className={pozor}>
              Dielce sa zmenili — porez Rozpočtu neplatí. Prepočítaj porez.
            </p>
          ) : null}
          {maVysledok && vysledok?.aktualny && !vysledok.algoritmus ? (
            <p className={pozor}>
              Porez je vypočítaný starším spôsobom (bez dielcov na sebe
              a dorezania ako v Cut Rite) — Prepočítaj ho podľa Cut Rite.
            </p>
          ) : null}
          {maVysledok &&
          vysledok?.aktualny &&
          vysledok.algoritmus &&
          vysledok.algoritmus !== vysledok.algoritmus_aktualny ? (
            <p className={pozor}>
              Porez je vypočítaný staršou verziou (bez hlavného rezu
              a skladania pásov ako v Cut Rite) — po prepočítaní môže vyjsť
              menej tabúľ.
            </p>
          ) : null}
          {cutrite && !cutrite.aktualny ? (
            <p className={pozor}>
              Rezanie sa od odoslania do Cut Rite zmenilo — plán z Cut Rite
              do ceny nejde, kým zákazku nepošleš do Cut Rite znova.
            </p>
          ) : null}
          {cutrite?.ine_parametre ? <p className={pozor}>{cutrite.ine_parametre}</p> : null}
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <th
                  className="w-1/2 pb-1 font-medium"
                  title="Porez Rozpočtu — tabule · strata (odpad + zvyšky, % z plochy nakúpených tabúľ)"
                >
                  Rozpočet
                </th>
                <th
                  className="pb-1 text-right font-medium"
                  title="Plán z Cut Rite — tabule · odpad % (MAT1)"
                >
                  Cut Rite
                </th>
              </tr>
            </thead>
            <tbody>
              {riadky.map((r) => {
                const rozdiel =
                  r.porez && r.cutrite ? r.porez.pocet_tabul - r.cutrite.tabule_ks : null;
                // Názov na vlastnom riadku — v paneli (~320 px) by vedľa dvoch
                // čísel ostalo na názov ~90 px („Rozrobe…").
                return (
                  <Fragment key={r.kluc}>
                    <tr className="border-t">
                      <td colSpan={2} className="max-w-0 truncate pt-1" title={r.titulok}>
                        {r.nazov}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className={`whitespace-nowrap pb-1 font-mono ${
                          r.vCene === "porez" ? "font-semibold" : "text-muted-foreground"
                        }`}
                        title={r.vCene === "porez" ? "Toto % je v cene" : undefined}
                      >
                        {r.porez ? (
                          <>
                            {r.porez.pocet_tabul} · {pct1(r.porezPct ?? 0)}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td
                        className={`whitespace-nowrap pb-1 pl-2 text-right font-mono ${
                          r.vCene === "cutrite" ? "font-semibold" : "text-muted-foreground"
                        }`}
                        title={[
                          r.vCene === "cutrite" ? "Toto % je v cene" : "",
                          r.cutrite?.tabule
                            .map(
                              (t) =>
                                `${t.ks}× ${t.dlzka_mm}×${t.sirka_mm}${t.odrezok ? " (odrezok)" : ""}`,
                            )
                            .join(", ") ?? "",
                          rozdiel === null || rozdiel === 0
                            ? ""
                            : `Rozpočet o ${Math.abs(rozdiel)} ${Math.abs(rozdiel) === 1 ? "tabuľu" : "tabule"} ${rozdiel > 0 ? "viac" : "menej"} ako Cut Rite`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      >
                        {r.cutrite ? (
                          <>
                            {r.cutrite.tabule_ks} · {pct1(parseFloat(r.cutrite.odpad_pct))}
                            {rozdiel === null ? null : rozdiel === 0 ? (
                              <span
                                className="ml-1 text-emerald-700 dark:text-emerald-400"
                                aria-label="rovnako ako Rozpočet"
                              >
                                ✓
                              </span>
                            ) : (
                              <span
                                className="ml-1 text-amber-700 dark:text-amber-400"
                                aria-label="inak ako Rozpočet"
                              >
                                ≠
                              </span>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {(vysledok?.materialy ?? []).some((m) => m.neumiestnene_ks > 0) ? (
            <p className="text-xs text-destructive">
              ⚠ Niektoré dielce sa nezmestia na tabuľu — pozri PDF.
            </p>
          ) : null}
          <p className="text-[10px] text-muted-foreground">
            {pausal
              ? "Do ceny ide paušál z cenníka (uni 20 % / dekor 30 %). "
              : "Do ceny ide tučné číslo — plán z Cut Rite, kým sa rezanie nezmení, inak porez Rozpočtu; materiál bez nich má paušál z cenníka. "}
            {cutrite
              ? `Cut Rite: ${cutrite.nazov_suboru}${cutrite.dokoncene_at ? `, ${datum(cutrite.dokoncene_at)}` : ""}${cutrite.casti > 1 ? ` — zoznam ${cutrite.casti} častí, % celého zoznamu` : ""}. `
              : ""}
            Cena materiálu nesie prirážku zo straty tabule; práca sa účtuje len
            na m² dielcov.
          </p>
        </>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={maVysledok ? "outline" : "default"}
          onClick={handleSpusti}
          disabled={spusti.isPending}
          title="Rozloží plošné dielce na tabule ako Cut Rite a vypočítaný odpad použije v cene"
        >
          {maVysledok ? (
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          ) : (
            <Scissors className="mr-1.5 h-3.5 w-3.5" />
          )}
          {spusti.isPending
            ? "Počítam…"
            : maVysledok
              ? "Prepočítať"
              : "Vypočítať porez"}
        </Button>
        {maVysledok ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleZrus}
              disabled={zrus.isPending}
              title="Zruší výsledok porezu Rozpočtu — plán z Cut Rite ostáva"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Zrušiť
            </Button>
          </>
        ) : null}
      </div>

      {/* Dĺžkové — tyče, pracovné dosky. Read-only: počíta sa na serveri
          pri každom načítaní, nemá tlačidlá ani badge, lebo do cien
          nevstupuje (dĺžkový materiál sa účtuje podľa čistej dĺžky). */}
      <NarezSekcia variantaId={variantaId} />
      </div>
    </PanelObal>
  );
}

/**
 * V bočnom paneli s tabmi je titulkom tab „Rezanie", takže roleta by bola
 * druhá úroveň skladania — vtedy sa vykreslí len badge nad obsahom.
 */
function PanelObal({
  collapsible,
  badge,
  children,
}: {
  collapsible: boolean;
  badge: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  if (collapsible) {
    return (
      <CollapsibleSection
        title="Rezanie"
        action={badge}
        storageKey="porez"
        // Otvorené, kým si to user nezbalí — potom si to pamätá. Podmieňovať
        // to stavom porezu nemá zmysel: `defaultOpen` sa vyhodnotí pri mounte,
        // keď výsledok ešte nie je načítaný. Že je porez neaktuálny, ukáže
        // badge v hlavičke rolety aj zbalenej.
        defaultOpen
      >
        {children}
      </CollapsibleSection>
    );
  }
  return (
    <section className="px-2.5 py-1.5">
      {badge ? <div className="flex justify-end">{badge}</div> : null}
      {children}
    </section>
  );
}

function NarezSekcia({ variantaId }: { variantaId: string }): JSX.Element | null {
  const { data } = useNarezPlan(variantaId);
  const plan = data?.data;
  const materialy = plan?.materialy ?? [];
  // Bez dĺžkových materiálov nemá zmysel zaberať miesto prázdnou tabuľkou.
  if (materialy.length === 0) return null;

  return (
    <div className="space-y-1.5 border-t pt-2">
      <div
        className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
        title={`Krátenie tyčí — pílová drážka ${plan?.prerez_mm} mm, orez ${plan?.orez_mm} mm na každom konci (Nastavenia → Rezanie)`}
      >
        Dĺžkové (tyče)
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="pb-1 font-medium">Materiál</th>
            <th className="pb-1 text-right font-medium">Tyče</th>
            <th className="pb-1 text-right font-medium">Kusov</th>
            <th
              className="pb-1 text-right font-medium"
              title="Podiel nakúpenej DĹŽKY, ktorý sa nestal kusom (orez koncov + prerezy + zvyšky). Nie je to to isté číslo ako strata pri tabuliach, tá ide z plochy."
            >
              Strata
            </th>
          </tr>
        </thead>
        <tbody>
          {materialy.map((m: NarezMaterialItem) => (
            <tr key={m.material.id} className="border-t">
              <td
                className="max-w-0 truncate py-1 pr-2"
                title={`${m.material.nazov}${
                  m.stock_mm ? ` — formát ${m.stock_mm} mm` : " — bez formátu v cenníku"
                } · zvyšky ${m.zvysok_mm_celkom} mm`}
              >
                {m.material.nazov}
                {m.stock_mm ? "" : " ⚠"}
              </td>
              <td className="py-1 text-right font-mono">{m.pocet_tyci}</td>
              <td className="py-1 text-right font-mono">{m.celkom_kusov}</td>
              <td className="py-1 text-right font-mono">
                {formatPct(m.strata_pct, 1)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {materialy.some(
        (m) => m.prilis_dlhe_ks > 0 || m.prilis_siroke_ks > 0 || m.bez_dlzky_ks > 0,
      ) ? (
        <p className="text-xs text-destructive">
          ⚠ Niektoré kusy sa nedajú narezať (dlhšie/širšie než formát alebo bez
          dĺžky) — pozri PDF nárezového plánu.
        </p>
      ) : null}
      <p className="text-[10px] text-muted-foreground">
        Strata dĺžkových do ceny nevstupuje — materiál sa účtuje podľa čistej
        dĺžky dielca.
      </p>
    </div>
  );
}
